// externals
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// selectors and actions
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { BALLOON_FALLBACK_DEFAULT_AMOUNT } from '@makemydeal/dr-dash-store/actions/balloonFallbackActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import * as flexBuyActionCreators from '@makemydeal/dr-dash-store/actions/flexBuyActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { CALC_ENDPOINT_OWNED_BY_DISPATCHER_META } from '@makemydeal/dr-dash-store/actions/programOrchestrationActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

// components and formatters
import { dealLockHooks, flexBuyPercentOptions } from '@makemydeal/dr-dash-components';
import type { GenericObject } from '@makemydeal/dr-platform-types';
import { FlexBuyAvailableMonths, AccelerateVehicleCondition, BALLOON } from '@makemydeal/dr-platform-types';
import type { MenuReferenceType } from '@interstate/components/Menu';

// utils and types
import { mapOfferType } from './utils/getDealTypeDropdownLabels';
import type { GetDealTypeLabelParams } from './utils/getDealTypeLabels';
import { getDealTypeLabel } from './utils/getDealTypeLabels';

// constants
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { FLEX_BUY_DEAL, MONEY_FACTOR, MONEY_FACTOR_TYPE } from '../../constants';

type UseDealTypeDropdownOptions = {
    onDealTypeSelected?: (dealType: string, dealTypes: string[]) => void;
};

export const useDealTypeDropdown = ({ onDealTypeSelected }: UseDealTypeDropdownOptions = {}) => {
    const dispatch = useDispatch();

    const offer = useSelector(offerReduxSelectors.getOffer);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const monthlyPayment = useSelector(offerSelectors.getMonthlyPayment);
    const apr = useSelector(offerReduxSelectors.getSellRate);
    const months = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const totalAmountDue = useSelector(offerReduxSelectors.getCashTotalAmountDueAfterCredits) ?? 0;
    const amountPaidByCustomer = useSelector(offerReduxSelectors.getCashDueFromCustomer);
    const cashDown = useSelector(offerReduxSelectors.getDealDeposit);
    const downPayment =
        useSelector((state: GenericObject) => offerReduxSelectors.getDownPaymentByOfferType(state, currentOfferType)) ?? 0;
    const showLoader = useSelector(paymentSelectors.isCalculatingPayment);
    const rateType = useSelector(offerReduxSelectors.getRateType);
    const moneyFactor = useSelector(offerReduxSelectors.getMoneyFactor);
    const hasLeasing = useSelector(dealerSelectors.hasLeasing);
    const isCashEnabled = useSelector(dealerSelectors.isCashOfferEnabled);
    const isBalloonDealTypeEnabled = useSelector(dealerSelectors.isBalloonDealTypeEnabledForDealer);
    const fordFlexBuyEnabledDealer = useSelector(dealerSelectors.dealerFlexBuyEnabled);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const firstPaymentTerm = useSelector(offerReduxSelectors.getFirstPaymentTerm);
    const firstPaymentAmount = useSelector(offerReduxSelectors.getFirstPaymentAmount);
    const secondPaymentTerm = useSelector(offerReduxSelectors.getSecondPaymentTerm);
    const secondPaymentAmount = useSelector(offerReduxSelectors.getSecondPaymentAmount);
    const isFlexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const currentFlexBuyPayments = useSelector(offerReduxSelectors.getCurrentFlexBuyPayments);
    const dueAtSigning = useSelector(offerReduxSelectors.getDueAtSigning) ?? 0;

    const isEcommercePendingCancellation = useSelector(offerInfoSelectors.getEcommercePendingCancellationStatus);

    const isVPPTemplateProductsLoading = useSelector(vehicleProtectionSelectors.isVPPTemplateProductsLoading);
    const currentCalcEndpoint = useSelector((state: GenericObject) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, currentOfferType)
    );
    const calcEngineTransitionState = useSelector(baseDealSelectors.getCalcEngineTransitionState);
    const isAutoSelectProgramChecked = useSelector(baseDealSelectors.isAutoSelectProgramChecked) as boolean;
    const autoSelectCheckBoxState = useRef(false);

    const useNewTermIdStandard = useSelector(featureToggleSelectors.useNewTermIdStandard);
    const isCashDownPaymentFieldEnabled = useSelector(featureToggleSelectors.isCashDownPaymentFieldEnabled);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isPaymentCalculating = useSelector(paymentSelectors.isCalculatingPayment);
    const isFetchingPaymentValue = useSelector(paymentSelectors.isFetchingPayment);
    const useFetchSpinner = useSelector(featureToggleSelectors.useFetchBasedSpinnerMV);
    const isBalloonEnhancementEnabled = useSelector(featureToggleSelectors.isBalloonEnhancementEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);

    const balloonAmount = useSelector(offerReduxSelectors.getBalloonAmount);
    const buyRate = useSelector(offerReduxSelectors.getBuyRate);

    const [dealTypeDropdown, setDealTypeDropdown] = useState<null | HTMLElement>(null);
    const [menuWidth, setMenuWidth] = useState<number>(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isUpdateRequested, setIsUpdateRequested] = useState(false);
    const [isDealTypeChanged, setIsDealTypeChanged] = useState(false);
    const vehicleDetails = useSelector(offerReduxSelectors.getVehicleDetails);
    const vehicleType = vehicleDetails?.type;

    useEffect(() => {
        if (!showLoader && isUpdateRequested && dealTypeDropdown) {
            dealTypeDropdown.focus();
            setIsUpdateRequested(false);
        }
    }, [showLoader, isUpdateRequested, dealTypeDropdown]);

    const isMoneyFactor = rateType === MONEY_FACTOR_TYPE;
    const rateTypeLabel = isMoneyFactor ? ` ${MONEY_FACTOR}` : ` APR`;
    const rateTypeVal = isMoneyFactor ? moneyFactor : apr;
    const isFlexBuyEnabled = fordFlexBuyEnabledDealer;
    const selectedDealType = currentOfferType === FINANCE && isFlexBuyPayment ? FLEX_BUY_DEAL : currentOfferType;

    const dealTypeDropDownAnalyticsMap = {
        [FINANCE]: interactionAnalyticsActionCreators.clickDealTypeDropdownFinance,
        [LEASE]: interactionAnalyticsActionCreators.clickDealTypeDropdownLease,
        [CASH]: interactionAnalyticsActionCreators.clickDealTypeDropdownCash
    };

    const flexBuyDetails = useMemo(() => {
        // if payment grid is enabled and current flex buy payments is present:
        if (isFlexBuyPaymentGridEnabled && currentFlexBuyPayments && isFlexBuyPayment) {
            return {
                isFlexBuy: isFlexBuyPayment,
                firstTerm: currentFlexBuyPayments.firstTermPaymentDetail?.term,
                firstPayment: currentFlexBuyPayments.firstTermPaymentDetail?.paymentAmount,
                secondTerm: currentFlexBuyPayments.secondTermPaymentDetail?.term,
                secondPayment: currentFlexBuyPayments.secondTermPaymentDetail?.paymentAmount
            };
        }

        // no payment grid scenario:
        return {
            isFlexBuy: isFlexBuyPayment,
            firstTerm: firstPaymentTerm,
            firstPayment: firstPaymentAmount,
            secondTerm: secondPaymentTerm,
            secondPayment: secondPaymentAmount
        };
    }, [
        isFlexBuyPayment,
        isFlexBuyPaymentGridEnabled,
        currentFlexBuyPayments,
        firstPaymentTerm,
        firstPaymentAmount,
        secondPaymentTerm,
        secondPaymentAmount
    ]);

    const dealTypes = useMemo(
        () => [
            FINANCE,
            ...(isFlexBuyEnabled ? [FLEX_BUY_DEAL] : []),
            ...(hasLeasing ? [LEASE] : []),
            ...(isCashEnabled ? [CASH] : []),
            ...(isBalloonDealTypeEnabled ? [BALLOON] : [])
        ],
        [isFlexBuyEnabled, hasLeasing, isCashEnabled, isBalloonDealTypeEnabled]
    );

    const closeMenu = () => setIsOpen(false);

    const openMenu = (e: React.MouseEvent<HTMLElement>) => {
        setIsOpen(true);
        setDealTypeDropdown(e.currentTarget);
        setMenuWidth(e.currentTarget.clientWidth + 2);
    };

    const handleFlexBuyPayment = useCallback(
        (offerType: string, currentSelection: string) => {
            const shouldAddFlexBuy = offerType === FLEX_BUY_DEAL && !isFlexBuyPayment;
            // reset to base finance deal if flex buy formula previously added
            const shouldRemoveFlexBuy = isFlexBuyPayment;

            if (offerType === FLEX_BUY_DEAL) {
                autoSelectCheckBoxState.current = isAutoSelectProgramChecked;
                dispatch(baseDealActionCreators.setCalcEngineTransitionState({ flexBuy: currentCalcEndpoint }));
                dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, FINANCE));
                dispatch(baseDealActionCreators.setIsAutoSelectProgramChecked(false));
            }

            const defaultTerm = FlexBuyAvailableMonths.MONTH_66;
            const defaultFordFlexBuy = Number(flexBuyPercentOptions[defaultTerm][0].value);
            dispatch(
                flexBuyActionCreators.thunkUpdateOfferTypeToFlexBuy({ defaultFordFlexBuy, shouldAddFlexBuy, shouldRemoveFlexBuy })
            );

            if (currentSelection === FLEX_BUY_DEAL && calcEngineTransitionState.flexBuy) {
                dispatch(baseDealActionCreators.updateCalculationEndpointForFinanceAndLeaseType(calcEngineTransitionState.flexBuy));
                dispatch(baseDealActionCreators.setIsAutoSelectProgramChecked(autoSelectCheckBoxState.current));
            }
        },
        [dispatch, isFlexBuyPayment, offer, useNewTermIdStandard, currentCalcEndpoint, calcEngineTransitionState?.flexBuy]
    );

    const handleCashDealTransition = useCallback(
        (offerType: string, currentSelection: string) => {
            if (offerType === CASH) {
                // When switching to "Cash" deal type, preserve the current calculation engine endpoint for future transitions back to Finance or Lease.
                dispatch(baseDealActionCreators.setCalcEngineTransitionState({ cashDeal: currentCalcEndpoint }));
                dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes));
            } else if (currentSelection === CASH && calcEngineTransitionState.cashDeal) {
                // If switching from "Cash" deal type, reset the calculation engine if exists
                dispatch(
                    baseDealActionCreators.updateCalculationEndpointForFinanceAndLeaseType(calcEngineTransitionState.cashDeal)
                );

                const isRestoringToPQ = calcEngineTransitionState.cashDeal === CALC_ENDPOINTS.programQuotes;
                dispatch(baseDealActionCreators.setIsAutoSelectProgramChecked(isRestoringToPQ));
            }
        },
        [dispatch, currentCalcEndpoint, calcEngineTransitionState?.cashDeal]
    );

    const menuReferenceProps = useMemo(
        () => ({
            open: isOpen,
            anchorEl: dealTypeDropdown as MenuReferenceType,
            onClose: closeMenu
        }),
        [isOpen, dealTypeDropdown]
    );

    // True for the transitions this file moves the calculation endpoint on itself, which
    // `programInvalidationMiddleware` must then leave alone or both switch on one transition.
    const dropdownOwnsCalcEndpoint = useCallback(
        (offerType: string, currentSelection: string, isSwitchingToCash: boolean): boolean =>
            offerType === FLEX_BUY_DEAL ||
            (currentSelection === FLEX_BUY_DEAL && Boolean(calcEngineTransitionState.flexBuy)) ||
            isSwitchingToCash ||
            (currentSelection === CASH && Boolean(calcEngineTransitionState.cashDeal)) ||
            (vehicleType === AccelerateVehicleCondition.USED && offerType === LEASE),
        [calcEngineTransitionState, vehicleType]
    );

    const menuOptions = useMemo(() => {
        return dealTypes.map((offerType) => {
            const mappedOfferType = mapOfferType(offerType);

            return {
                value: offerType.toLowerCase(),
                label: offerType.charAt(0).toUpperCase() + offerType.slice(1),
                onSelect: () => {
                    // isFlexBuyPayment determines if flexBuy sub finance option selected
                    const currentSelection = currentOfferType === FINANCE && isFlexBuyPayment ? FLEX_BUY_DEAL : currentOfferType;
                    const isSwitchingToCash = mappedOfferType === CASH;

                    if (offerType !== currentSelection) {
                        handleFlexBuyPayment(offerType, currentSelection);

                        // When switching FROM Cash: restore calc engine endpoint before dispatching offer type,
                        // so paymentGridMiddleware sees the correct (non-Cash) state when it processes the action.
                        if (!isSwitchingToCash) {
                            handleCashDealTransition(offerType, currentSelection);
                        }

                        if (vehicleType && vehicleType === AccelerateVehicleCondition.USED && offerType === LEASE) {
                            dispatch(
                                offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, LEASE)
                            );
                        }

                        // For Balloon from Finance with enhancement enabled:
                        // Save Finance quote data for fallback UPQ retry
                        if (offerType === BALLOON && currentOfferType === FINANCE && isBalloonEnhancementEnabled) {
                            dispatch(
                                baseDealActionCreators.setCalcEngineTransitionState({
                                    ...calcEngineTransitionState,
                                    balloonFallbackFinanceData: {
                                        term: months,
                                        buyRate,
                                        sellRate: apr,
                                        balloonAmount: BALLOON_FALLBACK_DEFAULT_AMOUNT
                                    }
                                })
                            );
                        }

                        // Every transition below moves the calculation endpoint from this file, either just
                        // above or — for Cash — just after `updatedOfferType`. Because `UPDATED_OFFER_TYPE` is
                        // in `programInvalidationMiddleware`'s invalidating set, and a deal-type change is
                        // exempt from its Auto-Select gate, that middleware would switch the endpoint too:
                        // restoring out of Cash or FlexBuy back to Finance/Lease had its restored endpoint
                        // immediately overridden to program-quotes. Marking the action keeps these three
                        // special cases the sole owner, which is what F-2's git history asks for — a trigger
                        // removed once for this exact reason and silently re-added five days later.
                        //
                        // Cash-in and FlexBuy-in currently escape by accident rather than by rule (a Cash deal
                        // fails the supported-offer-type check, and FlexBuy only fails the FlexBuy check
                        // because an unrelated reducer happens to run first), so they are marked too.
                        const offerTypeAction = offerReduxActionCreators.updatedOfferType(mappedOfferType);
                        dispatch(
                            dropdownOwnsCalcEndpoint(offerType, currentSelection, isSwitchingToCash)
                                ? {
                                      ...offerTypeAction,
                                      meta: { ...offerTypeAction.meta, [CALC_ENDPOINT_OWNED_BY_DISPATCHER_META]: true }
                                  }
                                : offerTypeAction
                        );

                        // When switching TO Cash: dispatch updatedOfferType(CASH) first so paymentGridMiddleware's
                        // early-return guard (`if offerType === CASH`) fires before any grid-triggering endpoint
                        // changes, preventing a stale Finance PO call (race condition) with the active payment grid.
                        if (isSwitchingToCash) {
                            handleCashDealTransition(offerType, currentSelection);
                        }

                        onDealTypeSelected?.(offerType, dealTypes);
                        setIsUpdateRequested(true);
                        setIsDealTypeChanged(true);
                        const analyticsAction =
                            dealTypeDropDownAnalyticsMap[offerType as keyof typeof dealTypeDropDownAnalyticsMap];
                        if (analyticsAction) {
                            dispatch(analyticsAction());
                        }
                    }
                }
            };
        });
    }, [
        dealTypes,
        dispatch,
        currentOfferType,
        isFlexBuyPayment,
        handleFlexBuyPayment,
        onDealTypeSelected,
        handleCashDealTransition,
        dropdownOwnsCalcEndpoint
    ]);

    const labelParams: GetDealTypeLabelParams = {
        currentOfferType,
        totalAmountDue,
        amountPaidByCustomer,
        months,
        monthlyPayment,
        downPayment,
        rateTypeVal,
        rateTypeLabel,
        isFlexBuyPayment,
        flexBuyDetails,
        cashDown,
        isOnePayLease,
        dueAtSigning,
        balloonAmount,
        isCashDownPaymentFieldEnabled
    };

    const labelLines = useMemo(
        () => getDealTypeLabel(labelParams),
        [
            currentOfferType,
            totalAmountDue,
            amountPaidByCustomer,
            months,
            monthlyPayment,
            downPayment,
            rateTypeVal,
            rateTypeLabel,
            isFlexBuyPayment,
            flexBuyDetails,
            cashDown,
            balloonAmount,
            isCashDownPaymentFieldEnabled
        ]
    );

    return {
        dealTypes,
        selectedDealType,
        menuOptions,
        menuReferenceProps,
        menuWidth,
        openMenu,
        labelLines,
        labelParams,
        isLocked,
        isFlexBuyPayment,
        isExplicitLenderP1Enabled,
        isVPPTemplateProductsLoading,
        isPaymentLoading: useFetchSpinner ? isFetchingPaymentValue : isPaymentCalculating,
        isDealTypeChangeNoticeShown: isEcommercePendingCancellation && isDealTypeChanged,
        dismissDealTypeChangeNotice: () => setIsDealTypeChanged(false)
    };
};

export type DealTypeDropdownState = ReturnType<typeof useDealTypeDropdown>;
